import { LayerSpecification } from "@iventis/mapbox-gl";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { MapModuleLayer } from "../../../types/store-schema";
import { AggregateLayers, SublayerSignature, SubLayerType } from "../sublayer-types";
import { getAggregateLayerBySubLayerId, getAggregateLayerIdFromSubLayerId, isAggregateSubLayerLocal } from "../engine-mapbox-helpers";
import { commentsLayerId, internalMapLayerIds, IVENTIS_USER_LAYERS_SLOT_ID } from "../../constants";
import { SubLayerPlacement } from "./layer-ordering-types-and-constants";
import { getRemoteLayerId, removeLayerIdSuffixes } from "../../engine-generic";
import { getLocalLayerID } from "../../engine-generic-helpers";
import { orderDomainLayers } from "./layer-ordering-generic-helpers";

export function getBaseSubLayer(aggregateLayer: SublayerSignature[]): SublayerSignature {
    return (
        aggregateLayer?.find((layer) => layer.type === SubLayerType.BASE || layer.type === SubLayerType.EXTRUSION) ??
        aggregateLayer?.find((layer) => layer.type === SubLayerType.POLYGON_OUTLINE)
    );
}

/** Gets the layer id which is above a given layer in the map */
export function getAboveLayerId(
    subLayerId: string,
    aggregateLayers: AggregateLayers,
    mapboxLayers: LayerSpecification[],
    mapModuleLayers: MapModuleLayer[],
    remoteBaseLayerId: string
) {
    if (commentsLayerId === removeLayerIdSuffixes(subLayerId)) {
        return getBottomMostInternalLayerId(mapboxLayers);
    }

    const isLocalLayer = isAggregateSubLayerLocal(subLayerId, aggregateLayers);
    if (isLocalLayer) {
        return getAboveLayerIdForLocalLayer(subLayerId, aggregateLayers);
    }
    return getAboveLayerIdForRemoteLayer(subLayerId, remoteBaseLayerId, aggregateLayers, mapboxLayers, mapModuleLayers);
}

/** Gets the above layer id for a remote layer */
function getAboveLayerIdForRemoteLayer(
    subLayerId: string,
    layerId: string,
    aggregateLayers: AggregateLayers,
    mapboxLayers: LayerSpecification[],
    mapModuleLayers: MapModuleLayer[]
) {
    // Check if the layer is placed below the base layer, if so return the base layer
    const aggregateLayer = aggregateLayers[layerId];
    if (aggregateLayer == null) {
        return IVENTIS_USER_LAYERS_SLOT_ID;
    }

    const subLayer = aggregateLayer?.find((layer) => layer.id === subLayerId);
    const placement = isSubLayerAboveOrBelow(subLayer.type, false);
    if (placement === SubLayerPlacement.Below) {
        return aggregateLayers[layerId].find((l) => l.type === SubLayerType.BASE)?.id;
    }

    // Find the layer above the given layer in ordered map layers
    const orderedMapModuleLayers = orderDomainLayers(mapModuleLayers);
    return getBottomMostPresentInMapLayer(orderedMapModuleLayers, layerId, mapboxLayers, aggregateLayers);
}

/** Gets the above layer id for a local layer */
function getAboveLayerIdForLocalLayer(localSubLayerId: string, aggregateLayers: AggregateLayers) {
    // Get the base layer of the layer being added to the map
    const localAggregateLayer = getAggregateLayerBySubLayerId(aggregateLayers, localSubLayerId);
    const layerSubLayerType = localAggregateLayer.find((layer) => layer.id === localSubLayerId);
    const localBaseLayer = getBaseSubLayer(localAggregateLayer);

    // If layer is placed below the base layer, then return the base layer id
    const placement = isSubLayerAboveOrBelow(layerSubLayerType.type, localBaseLayer == null);
    if (placement === SubLayerPlacement.Below && layerSubLayerType !== localBaseLayer) {
        return localBaseLayer?.id;
    }

    // Get the base remote sub layers and then find the equivalent type of the layer being added to the map
    const remoteBaseLayerId = getRemoteLayerId(getAggregateLayerIdFromSubLayerId(localSubLayerId, aggregateLayers));
    const remoteAggregateLayer = aggregateLayers[remoteBaseLayerId];
    const remoteSubLayer = remoteAggregateLayer?.find((layer) => layer.type === layerSubLayerType.type);
    return remoteSubLayer?.id;
}

/** Returns whether a sub layer is above or below the base layer */
export function isSubLayerAboveOrBelow(subLayerType: SubLayerType, isBaseLayerMissing = false) {
    if (isBaseLayerMissing) {
        return SubLayerPlacement.Above;
    }

    switch (subLayerType) {
        case SubLayerType.LINE_ICON:
        case SubLayerType.LINE_TEXT:
        case SubLayerType.POLYGON_TEXT:
        case SubLayerType.POINT_TEXT:
        case SubLayerType.EXTRUSION:
        case SubLayerType.LINE_MODEL:
            return SubLayerPlacement.Above;
        case SubLayerType.LINE_OUTLINE:
        case SubLayerType.POLYGON_OUTLINE:
            return SubLayerPlacement.Below;
        case SubLayerType.BASE:
            return SubLayerPlacement.IsBaseLayer;
        default:
            throw new Error(`${subLayerType} not handled`);
    }
}

/** Recursively looks for a layer in the map which it is below */
export function getBottomMostPresentInMapLayer(
    orderedMapModuleLayers: MapModuleLayer[],
    layerId: string,
    mapboxLayers: LayerSpecification[],
    aggregateLayers: AggregateLayers,
    iterations = 1
) {
    const index = orderedMapModuleLayers.findIndex((layer) => layer.id === layerId);
    const aboveLayer = orderedMapModuleLayers[index + iterations];

    if (aboveLayer == null) {
        return IVENTIS_USER_LAYERS_SLOT_ID;
    }

    // See if layer is in the map
    const foundLayer = getBottomMostRelatedLayer(aggregateLayers, aboveLayer?.id, mapboxLayers);
    if (foundLayer) {
        return foundLayer;
    }

    // If not in the map then attempt to find the layer above the one we just looked for
    return getBottomMostPresentInMapLayer(orderedMapModuleLayers, layerId, mapboxLayers, aggregateLayers, iterations + 1);
}

/** Returns the id of the bottom most layer in the map (for example if a line has an outline layer it will return it's id) */
export function getBottomMostRelatedLayer(aggregateLayers: AggregateLayers, layerId: string, mapboxLayers: LayerSpecification[]) {
    // Local layer will always be below the remote layer
    const layers = aggregateLayers[getLocalLayerID(layerId)] ?? aggregateLayers[layerId];

    if (layers == null) {
        return undefined;
    }
    const layer = layers[0];
    // Model layers have a class which contain the bottom most related layer
    if (layer?.styleType === StyleType.Model || layer?.styleType === StyleType.LineModel) {
        return layer.layerClass.getBottomMostLayerId();
    }

    // Get all ids relating to above layer (includes borders, text etc.)
    const allRelatedSubLayerIds = layers.map((layer) => layer.id);
    // Find the bottom most id of all the related layers
    const bottomMostLayer = mapboxLayers.find((layer) => allRelatedSubLayerIds.includes(layer.id));

    return bottomMostLayer?.id;
}

/** Finds the bottom most internal layer which could be drawing nodes, rotation handle etc. */
export function getBottomMostInternalLayerId(mapboxLayers: LayerSpecification[]) {
    const internalLayer = mapboxLayers?.find((layer) => internalMapLayerIds.includes(layer.id));
    return internalLayer?.id;
}
